export type CameraFailureReason = 'unsupported' | 'denied' | 'unavailable' | 'in-use' | 'failed'

export type CameraAccessResult =
  | { readonly ok: true; readonly stream: MediaStream }
  | { readonly ok: false; readonly reason: CameraFailureReason }

export const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
}

type CameraMediaDevices = Pick<MediaDevices, 'getUserMedia'>

/**
 * Whether this browser exposes a camera API at all.
 *
 * Synchronous, prompts nothing, and reads no permission state — it answers only "would
 * `requestCameraStream()` fail with `unsupported`". A caller can therefore decide *before*
 * offering a scan action, rather than letting the user discover it by tapping one.
 */
export function cameraSupported(
  mediaDevices: CameraMediaDevices | undefined = navigator.mediaDevices,
): boolean {
  return mediaDevices?.getUserMedia !== undefined
}

/** Requests camera access at the point of use. Permission state remains browser-owned. */
export async function requestCameraStream(
  mediaDevices: CameraMediaDevices | undefined = navigator.mediaDevices,
): Promise<CameraAccessResult> {
  if (!cameraSupported(mediaDevices)) return { ok: false, reason: 'unsupported' }

  try {
    return { ok: true, stream: await mediaDevices.getUserMedia(CAMERA_CONSTRAINTS) }
  } catch (error) {
    return { ok: false, reason: cameraFailureReason(error) }
  }
}

export function stopCameraStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop()
}

export function cameraFailureReason(error: unknown): Exclude<CameraFailureReason, 'unsupported'> {
  const name =
    error instanceof DOMException
      ? error.name
      : typeof error === 'object' && error !== null && 'name' in error && typeof error.name === 'string'
        ? error.name
        : undefined

  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'unavailable'
  if (name === 'NotReadableError') return 'in-use'
  return 'failed'
}

/**
 * The torch, which the DOM lib does not know about.
 *
 * `torch` is an *image-capture* constrainable property, not a `MediaTrackConstraintSet` member,
 * so TypeScript's `lib.dom` types neither declare it nor let it through `applyConstraints()`.
 * The casts below are confined to this file for that reason — a caller working with the raw
 * track would have to repeat them at every site.
 *
 * **Capability first, and never assume.** `getCapabilities()` is itself absent on some engines
 * (every WebKit build at the time of writing), and a track that has one may still not list
 * `torch` — a front camera, a USB webcam, a desktop. `torchSupported()` answers that question
 * without touching the hardware, so the control can be *absent* rather than present and dead.
 */
type TorchCapableTrack = Omit<MediaStreamTrack, 'getCapabilities'> & {
  // Optional, unlike the DOM lib's required member: WebKit ships no `getCapabilities` at all, and
  // a type that promises one is exactly how this would become a `TypeError` inside a render.
  readonly getCapabilities?: () => MediaTrackCapabilities & { readonly torch?: boolean }
}

function torchTrack(stream: MediaStream): TorchCapableTrack | undefined {
  return stream.getVideoTracks()[0]
}

/**
 * **Call it on the track, never off it.** `getCapabilities` is a Web IDL operation with a brand
 * check on its receiver, so `const f = track.getCapabilities; f()` throws
 * `TypeError: Illegal invocation` on every Chromium engine — and the `catch` below reports a
 * throw as "no torch". Detaching the method therefore made this function return `false`
 * *unconditionally* on Android Chrome, the Android WebView and desktop Chrome, which is every
 * browser that has the lamp; WebKit already answered `false` at the guard above. The pill was
 * unreachable in production from the day it shipped, and the only symptom was a control that was
 * not there — indistinguishable from a phone with no lamp, which is exactly what the absent-
 * rather-than-inert posture promises.
 *
 * A plain-object mock cannot see this: an arrow function on an object literal has no brand check
 * and works detached. `camera.test.ts` uses a real class for that reason.
 */
export function torchSupported(stream: MediaStream): boolean {
  const track = torchTrack(stream)
  if (track?.getCapabilities === undefined) return false
  try {
    return track.getCapabilities().torch === true
  } catch {
    // Firefox throws rather than returning an empty dictionary. A throw is "no torch", not a bug.
    return false
  }
}

/**
 * Switches the torch and reports what actually happened.
 *
 * Returns the state the track is in afterwards, so a caller can render the real thing rather
 * than an optimistic one: a rejected `applyConstraints()` leaves the lamp off, and a toggle that
 * still drew itself lit would be the second control on this screen claiming an outcome it did
 * not get.
 */
export async function setTorch(stream: MediaStream, on: boolean): Promise<boolean> {
  const track = torchTrack(stream)
  if (track === undefined) return false
  try {
    await track.applyConstraints({ advanced: [{ torch: on }] } as unknown as MediaTrackConstraints)
    return on
  } catch {
    return false
  }
}
